import { useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { PhotoFrame } from '@/components/profile/PhotoFrame';
import { ButtonLink } from '@/components/ui/Button';
import { Reveal } from '@/components/ui/Reveal';
import { StickyCta } from '@/components/ui/StickyCta';
import { getCreatorContent } from '@/content/registry';
import { useDocumentTitle } from '@/lib/hooks';
import { loadJourney } from '@/lib/storage';
import styles from './ProfilePage.module.css';

export function ProfilePage() {
  const { slug = '' } = useParams();
  const content = getCreatorContent(slug);
  const invitationRef = useRef<HTMLElement | null>(null);

  // Mirrors the <details> element rather than driving it: the disclosure is
  // native, and this only exists so the summary can name what the next tap
  // will do ("See" / "Hide") instead of a single label that is wrong half the
  // time.
  const [valuesOpen, setValuesOpen] = useState(false);

  // Saved progress is read once, so the invitation can say "pick up where you
  // left off" rather than pretending she has never been here.
  const saved = useMemo(
    () => (content ? loadJourney(content.profile.id) : null),
    [content],
  );

  useDocumentTitle(
    content
      ? `${content.profile.displayName} — Are We Compatible?`
      : 'Not found — Are We Compatible?',
  );

  if (!content) return <Navigate to="/404" replace />;

  const { profile, questions } = content;
  const answeredCount = saved ? Object.keys(saved.answers).length : 0;
  const hasProgress = answeredCount > 0 && answeredCount < questions.length;

  const supporting = profile.photos.supporting.slice(0, 4);
  const invitationCta = hasProgress
    ? 'Pick up where I left off'
    : profile.journeyInvitation.cta;

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        {/*
          --- Introduction ---------------------------------------------------

          Three blocks rather than two, because the mobile and desktop orders
          differ. Source order is the mobile order — who he is, then his face,
          then his voice — and the desktop grid puts the identity and the
          paragraphs back into one column beside the portrait.
        */}
        <section className={styles.intro}>
          <div className={styles.identity}>
            <h1 className={styles.name}>{profile.displayName}</h1>

            <p className={styles.meta}>
              <span>{profile.age}</span>
              <span className={styles.metaDot} aria-hidden="true" />
              <span>{profile.location}</span>
              <span className={styles.metaDot} aria-hidden="true" />
              <span>{profile.occupation}</span>
            </p>
          </div>

          <div className={styles.primaryPhoto}>
            <PhotoFrame photo={profile.photos.primary} priority />
          </div>

          <div className={styles.introBody}>
            <p className={styles.opening}>{profile.opening}</p>

            <div className={styles.paragraphs}>
              {profile.introParagraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>

        {/* --- Supporting photos ------------------------------------------- */}
        {supporting.length > 0 ? (
          <section className={styles.gallery} aria-label={`More photos of ${profile.displayName}`}>
            {supporting.map((photo, index) => (
              <Reveal key={photo.id} delay={index * 80}>
                <PhotoFrame photo={photo} />
              </Reveal>
            ))}
          </section>
        ) : null}

        {/* --- Interests ---------------------------------------------------- */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Where my time goes</h2>
          <ul className={styles.chips}>
            {profile.interests.map((interest) => (
              <li key={interest} className={styles.chip}>
                {interest}
              </li>
            ))}
          </ul>
        </section>

        {/* --- Values ------------------------------------------------------- */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>My values, in practice</h2>
          <p className={styles.sectionLede}>
            A little more about the principles I try to live by.
          </p>

          {/*
            Ten cards is a lot of James to meet before anyone has decided they
            are interested, so the list is folded away — closed by default on
            every width. Native <details>, so the keyboard and screen-reader
            behaviour is the platform's rather than ours.
          */}
          <details
            className={styles.valuesDisclosure}
            onToggle={(event) => setValuesOpen(event.currentTarget.open)}
          >
            <summary className={styles.valuesSummary}>
              {valuesOpen ? 'Hide my values' : 'See my values'}
            </summary>

            {/*
              No Reveal on these, unlike the gallery above. The stagger was for
              cards arriving as you scroll past them; behind a disclosure it
              would make the list's visibility depend on an IntersectionObserver
              firing for content that was unrendered when it mounted — a
              fragile thing to put in front of the one list this control exists
              to show. The cards are unchanged; only the entrance is gone.
            */}
            <ul className={styles.values}>
              {profile.values.map((value) => (
                <li key={value.label} className={styles.value}>
                  {value.kanji ? (
                    <span className={styles.valueKanji} lang="ja" aria-hidden="true">
                      {value.kanji}
                    </span>
                  ) : null}
                  <span className={styles.valueLabel}>{value.label}</span>
                  <span className={styles.valueMeaning}>{value.meaning}</span>
                </li>
              ))}
            </ul>
          </details>

          {/*
            Progressive disclosure, and deliberately understated: a dashed
            outline and a small secondary link, so it never competes with the
            invitation at the foot of the page. Rendered only when there is
            actually something behind it.
          */}
          {profile.moreAbout && !profile.moreAbout.hidden ? (
            <aside className={styles.more} aria-labelledby="more-about-heading">
              <h3 id="more-about-heading" className={styles.moreTitle}>
                {profile.moreAbout.teaser.heading}
              </h3>
              <p className={styles.moreBody}>{profile.moreAbout.teaser.body}</p>
              <ButtonLink
                to={`/${profile.slug}/how-i-show-up`}
                variant="secondary"
                size="small"
                className={styles.moreAction}
                withArrow
              >
                {profile.moreAbout.teaser.cta}
              </ButtonLink>
            </aside>
          ) : null}
        </section>

        {/* --- Looking for --------------------------------------------------- */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>What I am looking for</h2>
          {/* "Being vague about this has never once helped anyone" was a rule
              handed to the reader on the way past. This says the same thing
              about James and asks nothing of her. */}
          <p className={styles.sectionLede}>
            I&rsquo;d rather be clear about the direction I&rsquo;m dating in.
          </p>

          <ul className={styles.lookingFor}>
            {profile.lookingFor.map((item) => (
              <li key={item} className={styles.lookingForItem}>
                <span className={styles.lookingForGlyph} aria-hidden="true">
                  ●
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* --- Invitation ---------------------------------------------------- */}
        <section
          className={styles.invitation}
          aria-labelledby="invitation-heading"
          ref={invitationRef}
        >
          <div className={styles.invitationInner}>
            {hasProgress ? (
              <p className={styles.resumeNote}>
                You are {answeredCount}{' '}
                {answeredCount === 1 ? 'question' : 'questions'} in. Your answers are
                still here.
              </p>
            ) : null}

            <h2 id="invitation-heading" className={styles.invitationTitle}>
              {profile.journeyInvitation.heading}
            </h2>

            <p className={styles.invitationBody}>{profile.journeyInvitation.body}</p>

            <div className={styles.invitationAction}>
              <ButtonLink to={`/${profile.slug}/questions`} size="large" block withArrow>
                {invitationCta}
              </ButtonLink>
            </div>

            <p className={styles.invitationMeta}>{profile.journeyInvitation.meta}</p>

            {/*
              The other legitimate answer to this page.

              Answering seventeen questions from a stranger is an unusual thing
              to be asked, and until now it was the only way to reach James at
              all — which quietly made the quiz a toll rather than an offer.
              Somebody who has read the page and simply wants to say hello can,
              and does not have to earn the right to first.

              Deliberately quieter than the button above it: a plain link, no
              card, no arrow. The quiz is still the interesting thing to do.
            */}
            <p className={styles.invitationAside}>
              Prefer to skip the questions?{' '}
              {/* `state` rather than a query parameter: it records which door
                  she came through without putting a tracking-shaped `?from=`
                  in a URL she might copy, share, or simply read. */}
              <Link
                className={styles.invitationAsideLink}
                to={`/${profile.slug}/contact`}
                state={{ from: 'profile' }}
              >
                Say hello
              </Link>
              .
            </p>
          </div>
        </section>
      </div>

      {/* --- Sticky invitation ------------------------------------------------
          The profile is long, and the one thing it is for lives at the very
          bottom of it. This carries that offer alongside the reading, and gets
          out of the way once she has arrived at the real one. */}
      <StickyCta standInFor={invitationRef}>
        <ButtonLink to={`/${profile.slug}/questions`} size="large" block withArrow>
          {invitationCta}
        </ButtonLink>
      </StickyCta>
    </div>
  );
}
